import {Button, ButtonGroup, closeDialog, openDialog, Segment} from 'platform/components';
import {Show, Text, VStack} from 'platform/foundation';

import {isNotNil} from 'ramda';

import {CompositionPhotoLayer} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {PhotoCropDialog} from './PhotoCropDialog';

interface PhotoLayerFieldsProps extends TestIdProps {
  layer: CompositionPhotoLayer;
  onChange: (patch: Partial<CompositionPhotoLayer>) => void;
}

const FIT_OPTIONS: {value: CompositionPhotoLayer['fit']; label: string}[] = [
  {value: 'contain', label: i18n.t('entity.watermark.composition.labels.fitContain')},
  {value: 'cover', label: i18n.t('entity.watermark.composition.labels.fitCover')},
];

const PHOTO_CROP_DIALOG_ID = 'composition-editor-photo-crop';

export function PhotoLayerFields(props: PhotoLayerFieldsProps) {
  const openCropDialog = () =>
    openDialog(
      <PhotoCropDialog
        crop={props.layer.crop}
        onConfirm={(crop) => {
          props.onChange({crop});
          closeDialog(PHOTO_CROP_DIALOG_ID);
        }}
        onCancel={() => closeDialog(PHOTO_CROP_DIALOG_ID)}
        data-testid={suffixTestId('cropDialog', props)}
      />,
      {
        id: PHOTO_CROP_DIALOG_ID,
        size: 'large',
        title: i18n.t('entity.promoPhotos.composition.actions.cropPhoto'),
        'data-testid': suffixTestId('cropDialog', props),
      }
    );

  return (
    <VStack spacing={3}>
      <Text size="small" color="secondary">
        {i18n.t('entity.promoPhotos.composition.labels.vehiclePhotoDescription')}
      </Text>
      <Segment<CompositionPhotoLayer['fit']>
        label={i18n.t('entity.watermark.composition.labels.fit')}
        value={props.layer.fit}
        options={FIT_OPTIONS}
        onChange={(fit) => props.onChange({fit})}
        data-testid={suffixTestId('fit', props)}
      />
      <ButtonGroup>
        <Button
          variant="secondary"
          leftIcon="image/crop"
          title={i18n.t('entity.promoPhotos.composition.actions.cropPhoto')}
          onClick={openCropDialog}
          data-testid={suffixTestId('crop', props)}
        />
        <Show when={isNotNil(props.layer.crop)}>
          <Button
            variant="ghost"
            title={i18n.t('entity.promoPhotos.composition.actions.resetCrop')}
            onClick={() => props.onChange({crop: undefined})}
            data-testid={suffixTestId('resetCrop', props)}
          />
        </Show>
      </ButtonGroup>
    </VStack>
  );
}
